import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { SyntaxHighlighter } from '@/components/ui/syntax-highlighter';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BASE_URL_BACKEND, PATH_DOCS } from '@/constants';
import { ArrowTopRightIcon } from '@radix-ui/react-icons';
import { Link } from 'react-router-dom';
// import RegisWaitingList from './regis-waiting-list';
import siteConfig from '@/config';
import DisplayToken from '../tokens/displayToken';
import { useServerlessStore } from '@/store/serverless';
import { fTokenServerless } from '@/utils/formatLabel';
import { useState } from 'react';
const Onboarding = () => {
  const { token } = useServerlessStore();
  const [installMethod, setInstallMethod] = useState<string>('vscode');
  return (
    <>
      <div className="container">
        <div className="mt-8 self-center justify-self-center text-center text-lg font-semibold">
          Quickstart Guide
        </div>
        <div className="mx-auto mt-4 flex max-w-[800px] flex-col gap-4">
          <Card className="border-4">
            <CardHeader className="pb-0" />
            <CardContent>
              <DisplayToken />
            </CardContent>
          </Card>
          {/* <RegisWaitingList /> */}

          {/* 1 */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base">
                Step 1: Install Float16 CLI
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {/* <CardDescription>
                install our command-line interface (CLI) tool using pip
              </CardDescription>
              <SyntaxHighlighter language="bash" showCopy>
                {`pip install float16`}
              </SyntaxHighlighter> */}
              <Tabs defaultValue="vscode" onValueChange={setInstallMethod}>
                <TabsList variant={'outline'}>
                  <TabsTrigger value="vscode" variant={'outline'}>
                    VS Code (Recommended)
                  </TabsTrigger>
                  <TabsTrigger value="mac-os" variant={'outline'}>
                    macOS
                  </TabsTrigger>
                  <TabsTrigger value="windows" variant={'outline'}>
                    windows
                  </TabsTrigger>
                  <TabsTrigger value="npm" variant={'outline'}>
                    npm
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="mac-os" className="space-y-2">
                  <CardDescription>
                    You can also install the CLI via npm:
                  </CardDescription>
                  <SyntaxHighlighter language="bash" showCopy>
                    {`npm install -g npm i @float16-cloud/cli`}
                  </SyntaxHighlighter>
                </TabsContent>
                <TabsContent value="windows" className="space-y-2">
                  <CardDescription>
                    You can also install the CLI via npm:
                  </CardDescription>
                  <SyntaxHighlighter language="bash" showCopy>
                    {`npm install -g npm i @float16-cloud/cli`}
                  </SyntaxHighlighter>
                </TabsContent>
                <TabsContent value="npm" className="space-y-2">
                  <CardDescription>
                    You can also install the CLI via npm:
                  </CardDescription>
                  <SyntaxHighlighter language="bash" showCopy>
                    {`npm install -g npm i @float16-cloud/cli`}
                  </SyntaxHighlighter>
                </TabsContent>
                <TabsContent value="vscode" className="space-y-2">
                  <CardDescription>
                    Install the Float16 extension directly in VS Code:
                  </CardDescription>
                  <Link
                    to="https://marketplace.visualstudio.com/items?itemName=float16.float16-cli-tools"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      variant={'outline'}
                      className="w-full mt-2"
                      endIcon={<ArrowTopRightIcon />}
                    >
                      Install from VS Code Marketplace
                    </Button>
                  </Link>
                  <Alert variant="info" className="!mt-4">
                    With VS Code extension, you can skip Step 1.5, 2, 3, and 4. The extension handles authentication and project setup automatically.
                  </Alert>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {installMethod !== 'vscode' && siteConfig.selfHosted && (
            <>
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-base">
                    Step 1.5: Set Base URL
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <CardDescription>
                    Set the base URL of your serverless GPU service.
                  </CardDescription>
                  <SyntaxHighlighter language="bash" showCopy>
                    {`float16 config set --base-url ${BASE_URL_BACKEND}`}
                  </SyntaxHighlighter>
                  {/* <Alert variant="info" className="!mt-4">
                Find your token in the account settings page. <br />
                Need a new token? Go to Settings → Tokens → Generate New Token.
              </Alert> */}
                </CardContent>
              </Card>
            </>
          )}

          {installMethod !== 'vscode' && (
            <>
              {/* 2 */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-base">
                    Step 2: Authentication
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <CardDescription>
                    authenticate using your Float16 token.
                  </CardDescription>
                  <SyntaxHighlighter
                    language="bash"
                    showCopy
                    valueCopy={`float16 login --token ${token}`}
                  >
                    {`float16 login --token ${fTokenServerless(token || '')}`}
                  </SyntaxHighlighter>
                  {/* <Alert variant="info" className="!mt-4">
                Find your token in the account settings page. <br />
                Need a new token? Go to Settings → Tokens → Generate New Token.
              </Alert> */}
                </CardContent>
              </Card>

              <Tabs defaultValue="dev">
            <TabsList variant={'outline'}>
              <TabsTrigger value="dev" variant={'outline'}>
                Development
              </TabsTrigger>
              <TabsTrigger value="deploy" variant={'outline'}>
                Deployment
              </TabsTrigger>
            </TabsList>

            <TabsContent value="dev" className="space-y-4">
              {/* 3 */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-base">
                    Step 3: Set Up Your Project
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Tabs defaultValue="b">
                    <span className="mr-2 text-muted-foreground">
                      Start with
                    </span>
                    <TabsList>
                      <TabsTrigger value="b">an Example</TabsTrigger>
                      <TabsTrigger value="a">Your Own Project</TabsTrigger>
                    </TabsList>
                    <TabsContent value="a" className="space-y-2">
                      <CardDescription>
                        1. Navigate to your project directory
                      </CardDescription>
                      <SyntaxHighlighter showCopy>
                        {`cd your-project-directory`}
                      </SyntaxHighlighter>
                      <CardDescription>2. Create new project.</CardDescription>
                      <SyntaxHighlighter language="bash" showCopy>
                        {`float16 project create --instance <INSTANCE_TYPE>`}
                      </SyntaxHighlighter>
                      <CardDescription>
                        This creates:{' '}
                        <ul className="list-inside list-disc text-muted-foreground">
                          <li>
                            <code>float16.conf</code> - Configuration file for
                            your project
                          </li>
                          <li>
                            <code>requirements.txt</code> - Python dependencies
                            file
                          </li>
                        </ul>
                      </CardDescription>
                    </TabsContent>
                    <TabsContent value="b" className="space-y-2">
                      <CardDescription>
                        1. Clone our example project
                      </CardDescription>
                      <SyntaxHighlighter language="bash" showCopy>
                        {`float16 example official/run/helloworld`}
                      </SyntaxHighlighter>
                      <CardDescription>
                        2. Navigate to the example directory
                      </CardDescription>
                      <SyntaxHighlighter language="bash" showCopy>
                        {`cd helloworld`}
                      </SyntaxHighlighter>
                      <CardDescription>3. Create new project.</CardDescription>
                      <SyntaxHighlighter language="bash" showCopy>
                        {`float16 project create --instance <INSTANCE_TYPE>`}
                      </SyntaxHighlighter>
                      <CardDescription>
                        This update:
                        <ul className="list-inside list-disc text-muted-foreground">
                          <li>
                            <code>float16.conf</code> - Update project ID of the
                            project
                          </li>
                        </ul>
                      </CardDescription>
                      <CardDescription className="flex justify-end">
                        <Link
                          to="https://github.com/float16-cloud/examples"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button
                            variant={'link'}
                            endIcon={<ArrowTopRightIcon />}
                          >
                            Go to our examples
                          </Button>
                        </Link>
                      </CardDescription>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
              {/* 4 */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-base">
                    Step 4: Start a Project Session
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <CardDescription>
                    Initialize development environment
                  </CardDescription>
                  <SyntaxHighlighter language="bash" showCopy>
                    {`float16 project start`}
                  </SyntaxHighlighter>
                </CardContent>
              </Card>
              {/* 5 */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-base">
                    Step 5: Run your code
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <CardDescription>
                    Run your code on our cloud infrastructure
                  </CardDescription>
                  <SyntaxHighlighter language="bash" showCopy>
                    {`float16 run <YOUR_FILE.py> --name example`}
                  </SyntaxHighlighter>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="deploy" className="space-y-4">
              {/* 3 */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-base">
                    Step 3: Set Up Your Project
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Tabs defaultValue="b">
                    <span className="mr-2 text-muted-foreground">
                      Start with
                    </span>
                    <TabsList>
                      <TabsTrigger value="b">an Example</TabsTrigger>
                      <TabsTrigger value="a">Your Own Project</TabsTrigger>
                    </TabsList>
                    <TabsContent value="a" className="space-y-2">
                      <CardDescription>
                        1. Navigate to your project directory
                      </CardDescription>
                      <SyntaxHighlighter showCopy>
                        {`cd your-project-directory`}
                      </SyntaxHighlighter>
                      <CardDescription>2. Create new project.</CardDescription>
                      <SyntaxHighlighter language="bash" showCopy>
                        {`float16 project create --instance <INSTANCE_TYPE>`}
                      </SyntaxHighlighter>
                      <CardDescription>
                        This creates:{' '}
                        <ul className="list-inside list-disc text-muted-foreground">
                          <li>
                            <code>float16.conf</code> - Configuration file for
                            your project
                          </li>
                          <li>
                            <code>requirements.txt</code> - Python dependencies
                            file
                          </li>
                        </ul>
                      </CardDescription>
                    </TabsContent>
                    <TabsContent value="b" className="space-y-2">
                      <CardDescription>
                        1. Clone our example project
                      </CardDescription>
                      <SyntaxHighlighter language="bash" showCopy>
                        {`float16 example official/deploy/fastapi-helloworld`}
                      </SyntaxHighlighter>
                      <CardDescription>
                        2. Navigate to the example directory
                      </CardDescription>
                      <SyntaxHighlighter language="bash" showCopy>
                        {`cd fastapi-helloworld`}
                      </SyntaxHighlighter>
                      <CardDescription>3. Create new project.</CardDescription>
                      <SyntaxHighlighter language="bash" showCopy>
                        {`float16 project create --instance <INSTANCE_TYPE>`}
                      </SyntaxHighlighter>
                      <CardDescription>
                        This update:
                        <ul className="list-inside list-disc text-muted-foreground">
                          <li>
                            <code>float16.conf</code> - Update project ID of the
                            project
                          </li>
                        </ul>
                      </CardDescription>
                      <CardDescription className="flex justify-end">
                        <Link
                          to="https://github.com/float16-cloud/examples"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button
                            variant={'link'}
                            endIcon={<ArrowTopRightIcon />}
                          >
                            Go to our examples
                          </Button>
                        </Link>
                      </CardDescription>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
              {/* 4 */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle className="text-base">
                    Step 4: Deploy application
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <SyntaxHighlighter language="bash" showCopy>
                    {`float16 deploy <YOUR_FILE.py>`}
                  </SyntaxHighlighter>
                  <CardDescription>
                    System will return:
                    <ul className="list-inside list-disc text-muted-foreground">
                      <li>Endpoint URL for accessing your application</li>
                      <li>API key for authentication</li>
                    </ul>
                  </CardDescription>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
            </>
          )}

          <Link
            to={PATH_DOCS.root}
            className="w-full"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant={'secondary'} className="w-full">
              Go to Docs
              <ArrowTopRightIcon />
            </Button>
          </Link>
        </div>
      </div>
    </>
  );
};

export default Onboarding;
